import { Input, Modal, Form, Button, Progress, List, message } from "antd";
import { useState } from "react";
import { sessionService } from "../services/session.service";
import { PlusOutlined, CopyOutlined } from "@ant-design/icons";

interface AddSessionsModalProps {
  onSuccess?: () => void;
}

interface SessionStatus {
  username: string;
  status: "pending" | "success" | "error";
  error?: string;
}

const SESSION_REGEX = /^[^|]+\|[^|]+\|[^|]+(?:\|[^|]+)*$/;

export default function AddSessionsModal({ onSuccess }: AddSessionsModalProps) {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sessionStatuses, setSessionStatuses] = useState<SessionStatus[]>([]);
  const [progress, setProgress] = useState(0);

  const validateSessions = (
    sessions: string[]
  ): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const uniqueUsernames = new Set<string>();

    if (sessions.length === 0) {
      errors.push("Please enter at least one session");
      return { valid: false, errors };
    }

    sessions.forEach((session, index) => {
      const trimmedSession = session.trim();

      if (!trimmedSession) {
        errors.push(`Session at line ${index + 1} is empty`);
        return;
      }

      if (!SESSION_REGEX.test(trimmedSession)) {
        errors.push(
          `Session at line ${
            index + 1
          } is invalid. Format should be: username|password|otpSecret (additional fields are optional)`
        );
        return;
      }

      const [username] = trimmedSession.split("|");
      if (uniqueUsernames.has(username)) {
        errors.push(`Username "${username}" is duplicated`);
      } else {
        uniqueUsernames.add(username);
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const handleSubmit = async (values: { sessions: string }) => {
    const sessions = values.sessions
      .split("\n")
      .filter((session) => session.trim());

    const { valid, errors } = validateSessions(sessions);

    if (!valid) {
      errors.forEach((error) => message.error(error));
      return;
    }

    setLoading(true);
    setSessionStatuses(
      sessions.map((session) => ({
        username: session.split("|")[0],
        status: "pending",
      }))
    );
    setProgress(0);

    for (let i = 0; i < sessions.length; i++) {
      const [username, password, otpSecret] = sessions[i].trim().split("|");
      try {
        await sessionService.createSession({ username, password, otpSecret });
        setSessionStatuses((prev) =>
          prev.map((status, index) =>
            index === i ? { ...status, status: "success" } : status
          )
        );
      } catch (error) {
        setSessionStatuses((prev) =>
          prev.map((status, index) =>
            index === i
              ? { ...status, status: "error", error: "Failed to add session" }
              : status
          )
        );
        message.error(`Failed to add session: ${username}`);
        break;
      }
      setProgress(((i + 1) / sessions.length) * 100);
    }

    setLoading(false);
    if (progress === 100) {
      message.success("All sessions added successfully");
      onSuccess?.();
    }
  };

  const reset = (shouldClose?: boolean) => {
    form.resetFields();
    setSessionStatuses([]);
    setProgress(0);
    if (shouldClose) {
      setOpen(false);
    }
  };

  const handleCopyFailedSessions = () => {
    const failedSessions = sessionStatuses
      .filter((status) => status.status === "error")
      .map((status) => status.username)
      .join("\n");

    if (failedSessions) {
      navigator.clipboard.writeText(failedSessions);
      message.success("Failed sessions copied to clipboard");
    } else {
      message.info("No failed sessions to copy");
    }
  };

  return (
    <>
      <Button
        icon={<PlusOutlined />}
        type="primary"
        onClick={() => setOpen(true)}
      >
        Add Sessions
      </Button>
      <Modal
        title="Add Sessions"
        open={open}
        onCancel={() => {
          if (sessionStatuses.length === 0 || progress === 100) {
            reset(true);
          } else {
            message.warning("Please wait for the process to complete");
          }
        }}
        footer={null}
        width={600}
        closable={sessionStatuses.length === 0 || progress === 100}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          {sessionStatuses.length === 0 && (
            <>
              <Form.Item
                label="Sessions"
                name="sessions"
                rules={[
                  { required: true, message: "Please enter sessions" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const sessions = value
                        .split("\n")
                        .filter((session: string) => session.trim());
                      const { valid, errors } = validateSessions(sessions);
                      if (!valid) {
                        return Promise.reject(errors[0]);
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                help="Enter one session per line. Format: username|password|otpSecret (additional fields are optional)"
              >
                <Input.TextArea
                  rows={10}
                  placeholder="Enter sessions (one per line)"
                />
              </Form.Item>

              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button onClick={() => reset(true)} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Add Sessions
                </Button>
              </div>
            </>
          )}

          {sessionStatuses.length > 0 && (
            <>
              <Progress
                percent={progress}
                status={progress === 100 ? "success" : "active"}
                format={() =>
                  `${
                    sessionStatuses.filter((s) => s.status !== "pending").length
                  }/${sessionStatuses.length}`
                }
              />
              <List
                size="small"
                bordered
                dataSource={sessionStatuses}
                renderItem={(item) => (
                  <List.Item>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{item.username}</span>
                      <span
                        style={{
                          color:
                            item.status === "success"
                              ? "green"
                              : item.status === "error"
                              ? "red"
                              : "orange",
                        }}
                      >
                        {item.status === "success"
                          ? "✓"
                          : item.status === "error"
                          ? "✗"
                          : "..."}
                      </span>
                    </div>
                  </List.Item>
                )}
              />

              {progress === 100 && (
                <div style={{ marginTop: 16, textAlign: "right" }}>
                  {sessionStatuses.some(
                    (status) => status.status === "error"
                  ) && (
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleCopyFailedSessions}
                      style={{ marginRight: 8 }}
                    >
                      Copy Failed Sessions
                    </Button>
                  )}
                  <Button
                    onClick={() => reset(true)}
                    style={{ marginRight: 8 }}
                  >
                    Close
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => reset()}
                    disabled={sessionStatuses.length === 0}
                  >
                    Add More
                  </Button>
                </div>
              )}
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
